import { useEffect, useMemo, useState } from "react";
import { FleetVehicleExpenseStats } from "services/api/fleet/vehicles/expenses/types";
import { Loader } from "ui/components/Loader";
import VehicleExpenseChart, { ExpenseChartBucket } from "../VehicleExpenseChart/VehicleExpenseChart";
import VehicleExpenseStatsHeader from "../VehicleExpenseStatsHeader/VehicleExpenseStatsHeader";

interface VehicleExpenseMonthlyProps {
    fetchStats: () => Promise<FleetVehicleExpenseStats>;
    title?: string;
    subtitle?: string;
}

const MONTH_NAMES = ["Sij", "Velj", "Ožu", "Tra", "Svi", "Lip", "Srp", "Kol", "Ruj", "Lis", "Stu", "Pro"];

const parseMonth = (mmYyyy: string): { month: number; year: number; date: Date } => {
    const [mm, yyyy] = mmYyyy.split("/").map(Number);
    return { month: mm || 1, year: yyyy || 0, date: new Date(yyyy, (mm || 1) - 1, 1) };
};

const emptyStats: FleetVehicleExpenseStats = {
    totalAmount: 0,
    months: [],
    weeks: [],
    sellingPriceHistory: [],
};

const VehicleExpenseMonthly: React.FC<VehicleExpenseMonthlyProps> = ({ fetchStats, title, subtitle }) => {
    const [stats, setStats] = useState<FleetVehicleExpenseStats | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        (async () => {
            try {
                const result = await fetchStats();
                if (!cancelled) setStats(result);
            } catch {
                if (!cancelled) setStats(emptyStats);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [fetchStats]);

    const data = stats ?? emptyStats;
    const months = data.months ?? [];

    const buckets: ExpenseChartBucket[] = useMemo(() => months.map(m => {
        const parsed = parseMonth(m.month);
        return {
            label: MONTH_NAMES[parsed.month - 1] ?? "",
            items: m.items ?? [],
            cumulative: m.amount ?? 0,
        };
    }), [months]);

    const { windowStart, windowEndDate } = useMemo(() => {
        if (months.length === 0) {
            const now = new Date();
            return { windowStart: now, windowEndDate: now };
        }
        const first = parseMonth(months[0].month).date;
        const last = parseMonth(months[months.length - 1].month).date;
        return {
            windowStart: first,
            windowEndDate: new Date(last.getFullYear(), last.getMonth() + 1, 1),
        };
    }, [months]);

    const tooltipTitle = (b: ExpenseChartBucket) => {
        const idx = buckets.indexOf(b);
        const m = months[idx];
        if (!m) return b.label;
        const yyyy = m.month.split("/")[1] ?? "";
        return `${b.label} ${yyyy}`;
    };

    if (isLoading || !stats) {
        return (
            <div className="relative flex-1 h-full">
                <Loader isLoading={true}>{null}</Loader>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full pt-4">
            <VehicleExpenseStatsHeader stats={data} title={title} subtitle={subtitle ?? "Kumulativni pregled po mjesecima do trenutnog mjeseca"} />
            <VehicleExpenseChart
                buckets={buckets}
                windowStart={windowStart}
                windowEndDate={windowEndDate}
                priceHistory={data.sellingPriceHistory ?? []}
                bucketTooltipTitle={tooltipTitle}
            />
        </div>
    );
};

export default VehicleExpenseMonthly;
